import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { essentialsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { SIGNUP_TOKEN_KEY } from './RegisterPage';
import ThemeToggle from '../components/ThemeToggle';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';

/**
 * THE GATE. Sign-up asks for an email and a password; this asks the three things it no
 * longer does, and nobody gets past it without answering.
 *
 * ⚠️ IT SITS BEFORE EMAIL CONFIRMATION, DELIBERATELY (Rob's option B, 15/08/2026):
 *   • the confirmation email can then greet them by name and mention their company, on
 *     the one email in the journey most likely to be taken for spam; and
 *   • we learn who a lead is even if they never confirm — "Sam at Northgate Joinery
 *     started a trial and stopped" is worth acting on; `it@company.com` is not.
 *
 * ⚠️ The person here is NOT verified and NOT logged in. They hold a scoped token that can
 * reach this screen and nothing else (auth.SIGNUP_SCOPE). It is passed explicitly and
 * never stored as `token` — see the note in lib/api.js.
 *
 * ⚠️ Anyone who abandons this screen is swept up an hour later by
 * services/signup_sweep.py and sent the plain confirmation link. That is not a nicety:
 * Rob tested a competitor's version of this flow, stopped here, and never heard from them
 * again.
 */
const WelcomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useAuth();
  const signupToken = sessionStorage.getItem(SIGNUP_TOKEN_KEY) || null;

  const [form, setForm] = useState({ company_name: '', first_name: '', last_name: '' });
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(true);
  // Did Google or Microsoft already vouch for this address? Decides what we promise below.
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  // ⚠️ Set the moment the form is saved, so the check below does not run again on the way
  // out. Rob's log on 16/08/2026 showed a `GET /onboarding/essentials 403` immediately
  // after a successful PUT: the scoped token had just been cleared, the effect re-ran with
  // nothing to authenticate with, and the server quite rightly refused. Harmless, but a
  // 403 in a log is a thing somebody has to rule out later.
  const done = useRef(false);

  // ⚠️ ASK THE SERVER WHETHER THIS GATE IS STILL OWED, rather than assuming. Somebody can
  // arrive here already past it — a bookmark, a Back button, or the safety-net route where
  // they confirmed their email first. Sending them round a gate they have already answered
  // would be its own small insult, and re-asking invites a contradictory answer.
  useEffect(() => {
    if (done.current) return;
    let cancelled = false;
    essentialsApi.get(signupToken)
      .then((r) => {
        if (cancelled) return;
        if (r?.data && r.data.needed === false) {
          navigate('/onboarding', { replace: true });
          return;
        }
        setForm((f) => ({
          company_name: r?.data?.company_name || f.company_name,
          first_name: r?.data?.first_name || f.first_name,
          last_name: r?.data?.last_name || f.last_name,
        }));
        // ⚠️ WHAT WE PROMISE HERE DEPENDS ON HOW THEY GOT HERE. A provider sign-up gets no
        // confirmation email — Google or Microsoft has already proved the address — so the
        // old line "we'll send your confirmation email" was simply untrue for them (Rob
        // spotted it on screen, 16/08/2026). The server tells us which it is; the first
        // version of this guessed from whether a name was present, which is a guess where
        // an answer was available.
        setAlreadyVerified(Boolean(r?.data?.email_verified));
        setChecking(false);
      })
      .catch(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [signupToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await essentialsApi.save({
        company_name: form.company_name.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      }, signupToken);

      // The scoped token has done its one job. Drop it rather than leave it lying in the
      // tab — it is useless from here, and a token nobody needs is a token nobody is
      // watching. ⚠️ `done` first, so the check effect cannot fire again without it.
      done.current = true;
      sessionStorage.removeItem(SIGNUP_TOKEN_KEY);

      // ⚠️ A PROVIDER SIGN-UP COMES BACK WITH A REAL SESSION, and must be signed in with
      // it before going anywhere. Without this they arrive at /onboarding holding only the
      // scoped token — which has just been spent — so the protected route bounces them to
      // /login and asks them to sign in again, seconds after Google or Microsoft vouched
      // for them (Rob, 16/08/2026). A password sign-up gets no token here, deliberately:
      // its confirmation link is still the gate.
      if (res?.data?.access_token) {
        loginWithToken(res.data);
        navigate('/onboarding', { replace: true });
      } else if (res?.data?.email_sent) {
        navigate('/verify-email', {
          replace: true,
          state: { sentTo: res?.data?.email || location.state?.email },
        });
      } else {
        // Already verified but no session handed back — the safety-net route, where they
        // are properly logged in already.
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save that — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const ready = form.company_name.trim() && form.first_name.trim() && form.last_name.trim();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <BrandedToaster />
      <ThemeToggle iconOnly variant="ghost" className="fixed top-4 right-4 z-50 text-muted-foreground hover:text-foreground" />

      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo size={34} />
        </div>

        <div className="card-hive p-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Welcome to HolidayHive</h1>
          <p className="text-muted-foreground mb-8">
            {alreadyVerified
              ? "Just one or two things and you're in."
              : "Just three things and we'll send your confirmation email."}
          </p>

          {checking ? (
            <div className="py-8 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin" />
              One moment…
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="welcome-form">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company name</Label>
                <Input
                  id="company_name"
                  name="organization"
                  type="text"
                  autoComplete="organization"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="input-hive"
                  required
                  data-testid="welcome-company-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First name</Label>
                  <Input
                    id="first_name"
                    name="given-name"
                    type="text"
                    autoComplete="given-name"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="input-hive"
                    required
                    data-testid="welcome-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Surname</Label>
                  <Input
                    id="last_name"
                    name="family-name"
                    type="text"
                    autoComplete="family-name"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="input-hive"
                    required
                    data-testid="welcome-lastname-input"
                  />
                </div>
              </div>

              {/* ⚠️ NO SKIP, NO "do this later", AND NO WAY ROUND IT. That is the entire
                  point of this screen: sign-up creates the account without these, so the
                  only thing standing between the app and a nameless person on every
                  screen in it is this form being answered. */}
              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={saving || !ready}
                data-testid="welcome-submit-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
